import { FileDownload, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { FormSummaryModal } from "../components/dialogs/FormSummaryModal";
import { useAdminUsers } from "./hooks/useAdminUsers";

export default function AdminUsersPage() {
  const {
    users,
    loading,
    search,
    setSearch,
    sortBy,
    sortDir,
    handleSort,
    page,
    setPage,
    totalPages,
    handleExportCSV,
  } = useAdminUsers();

  const [openModal, setOpenModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [selectedSchema, setSelectedSchema] = useState<any>(null);

  const handleViewForm = async (schema: any, submission: any) => {
    setSelectedSchema(schema);
    setSelectedForm(submission);
    setOpenModal(true);
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        margin: "24px",
      }}
    >
      <CardHeader
        title="Usuarios registrados"
        subheader="Visualiza todos los usuarios, filtra y exporta datos"
        action={
          <Button
            startIcon={<FileDownload />}
            variant="outlined"
            size="small"
            onClick={handleExportCSV}
          >
            Exportar CSV
          </Button>
        }
      />
      <Divider />
      <CardContent>
        <Stack spacing={2}>
          <TextField
            size="small"
            placeholder="Buscar por nombre, correo o pasaporte..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{
                  maxWidth: "85vw",
                  mx: "auto",
                  overflowX: "auto",
                  borderRadius: 2,
                  boxShadow: "none",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        Usuario
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        Correo
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>Rol</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>Plan</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        Pasaporte
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        Registrado
                      </TableCell>

                      {/* 🔹 Nuevas columnas */}
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        Formulario
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        Actualizado
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        Finalizado
                      </TableCell>

                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {users.map((u) => {
                      const sub = u.submission;
                      const hasJson = !!sub?.json;
                      const hasSubmitted = !!sub?.submittedAt;
                      const hasFinished = !!sub?.finishedAt;

                      let statusLabel = "No iniciado";
                      let statusColor: "default" | "warning" | "success" =
                        "default";

                      if (hasJson && !hasFinished) {
                        statusLabel = "En proceso";
                        statusColor = "warning";
                      } else if (hasFinished) {
                        statusLabel = "Finalizado";
                        statusColor = "success";
                      }

                      return (
                        <TableRow key={u.userId}>
                          {/* 🧍 Usuario */}
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                            >
                              <Typography>{u.fullName}</Typography>
                            </Stack>
                          </TableCell>

                          {/* 📧 Correo */}
                          <TableCell>{u.email}</TableCell>

                          {/* 🧩 Rol */}
                          <TableCell>{u.role}</TableCell>

                          {/* 💳 Plan */}
                          <TableCell>
                            {u.plan ? (
                              <Box
                                sx={{
                                  display: "inline-block",
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: "12px",
                                  bgcolor: "success.main",
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: 13,
                                  textAlign: "center",
                                  minWidth: 80,
                                }}
                              >
                                {u.plan.name}
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: "inline-block",
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: "12px",
                                  bgcolor: "#f5f5f5",
                                  color: "#999",
                                  fontWeight: 600,
                                  fontSize: 13,
                                  textAlign: "center",
                                  minWidth: 80,
                                }}
                              >
                                Sin plan
                              </Box>
                            )}
                          </TableCell>

                          {/* 🪪 Pasaporte */}
                          <TableCell>{u.passport}</TableCell>

                          {/* 📅 Fecha de creación de la cuenta */}
                          <TableCell>
                            {new Date(u.createdAt!).toLocaleDateString("es-CO")}
                          </TableCell>

                          {/* 📄 Estado del formulario */}
                          <TableCell>
                            <Chip
                              label={statusLabel}
                              color={statusColor}
                              variant="filled"
                              size="small"
                              sx={{
                                fontWeight: 600,
                                color: "white",
                                px: 1,
                                minWidth: 95,
                                justifyContent: "center",
                              }}
                            />
                          </TableCell>

                          {/* 🕐 Fecha de creación del formulario */}
                          <TableCell>
                            {sub?.updatedAt
                              ? new Date(sub.updatedAt).toLocaleString("es-CO")
                              : "-"}
                          </TableCell>

                          {/* ✅ Fecha de finalización */}
                          <TableCell>
                            {hasFinished
                              ? new Date(sub!.finishedAt).toLocaleString(
                                  "es-CO"
                                )
                              : "-"}
                          </TableCell>

                          {/* ⚙️ Acciones */}

                          <TableCell>
                            {sub && (
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="contained"
                                  color="info"
                                  size="small"
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                  }}
                                  onClick={() =>
                                    handleViewForm(u.latestForm?.json, sub.json)
                                  }
                                >
                                  Ver
                                </Button>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* <Stack alignItems="center" sx={{ mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, val) => setPage(val)}
                  color="primary"
                />
              </Stack> */}
            </>
          )}
        </Stack>
      </CardContent>

      {selectedForm && selectedSchema && (
        <FormSummaryModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          schema={selectedSchema}
          submission={selectedForm}
        />
      )}
    </Card>
  );
}
